// noinspection PointlessBooleanExpressionJS
import { DateTime } from "luxon";
import { convertDateTimesToString } from "utils/apiHelpers";
import type { SubscriptionStatus } from "client/api-v2";
import type { EntityHistoryReferenceDto } from "client/api/EntityHistoryReferenceDto";
import axiosClient from "../axios-client";
import type { EntityDto } from "./EntityDto";
import type { EntitlementDto, IntLimitFeatureDto, MachineLimitFeatureDto, OverrunLimitFeatureDto } from "./FeatureApi";

export const UnlimitedTaskCap = "unlimited";

export interface CloudSubscriptionLicensingChannelDto {
  id: string;
  createdDate: DateTime;
  expiryDate: DateTime | null;
  trialId: string | null;
  salesforceContractId: string | null;
  zuoraSubscriptionKey: string | null;
  renewalPeriod: CloudSubscriptionRenewalPeriod | null;
  autoRenew: boolean | null;
  comment: string | null;
  subscriptionId: string | null;
  planName: string | null;
  status: SubscriptionStatus | null;
  termEndDate: DateTime | null;
}

export interface AllowedIpPrefixDto {
  prefix: string;
  description: string;
}

export interface IpAddressAllowListDto {
  active: boolean;
  allowedIpPrefixes: AllowedIpPrefixDto[];
}

export interface CloudSubscriptionDto extends EntityDto, SubscriptionGroupItem {
  id: string;
  serial: string;
  serialHash: string;
  isFulfilled: boolean;
  friendlyName: string;
  salesforceContractId: string;
  cloudRegionId: string;
  dnsPrefix: string;
  staticIpPrefixes?: string[];
  maintenanceWindow: MaintenanceWindowDto;
  isInstanceProvisioned: boolean;
  cloudSubscriptionStatus: CloudSubscriptionStatus;
  licensingChannel: CloudSubscriptionLicensingChannelDto;
  entitlements: Record<string, EntitlementDto>;
  staffSpecifiedTaskCap?: IntLimitFeatureDto | null;
  staffSpecifiedTaskCapExpiryUtc?: DateTime;
  hasProtectedUserGrants: boolean;
  ipAddressAllowList: IpAddressAllowListDto;
}

export enum CloudSubscriptionRenewalPeriod {
  Monthly = "Monthly",
  Annually = "Annually",
}

export const cloudSubscriptionRenewalPeriodDescriptions: Record<CloudSubscriptionRenewalPeriod, string> = {
  [CloudSubscriptionRenewalPeriod.Monthly]: "Renews monthly",
  [CloudSubscriptionRenewalPeriod.Annually]: "Renews annually",
};

export type MaintenanceWindowDto = {
  startTime: DateTime;
  endTime: DateTime;
  timeZone: string;
} & (
  | {
      frequency: "Daily";
    }
  | {
      frequency: "Weekly";
      dayOfWeek: "Sunday" | "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday";
    }
  | {
      frequency: "Monthly";
      weekOfMonth: "First" | "Second" | "Third";
      dayOfWeek: "Monday";
    }
);

export function mapMaintenanceWindowDtoToRequest(maintenanceWindow: MaintenanceWindowDto) {
  function getFrequency() {
    switch (maintenanceWindow.frequency) {
      case "Daily":
        return CloudSubscriptionOutageWindowFrequency.Daily;
        break;
      case "Weekly":
        switch (maintenanceWindow.dayOfWeek) {
          case "Monday":
            return CloudSubscriptionOutageWindowFrequency.MondayOfEachWeek;
            break;
          case "Tuesday":
            return CloudSubscriptionOutageWindowFrequency.TuesdayOfEachWeek;
            break;
          case "Wednesday":
            return CloudSubscriptionOutageWindowFrequency.WednesdayOfEachWeek;
            break;
          case "Thursday":
            return CloudSubscriptionOutageWindowFrequency.ThursdayOfEachWeek;
            break;
          case "Friday":
            return CloudSubscriptionOutageWindowFrequency.FridayOfEachWeek;
            break;
          case "Sunday":
            return CloudSubscriptionOutageWindowFrequency.SundayOfEachWeek;
            break;
        }
        break;
      case "Monthly":
        switch (maintenanceWindow.weekOfMonth) {
          case "First":
            return CloudSubscriptionOutageWindowFrequency.FirstMondayOfEachMonth;
            break;
          case "Second":
            return CloudSubscriptionOutageWindowFrequency.SecondMondayOfEachMonth;
            break;
          case "Third":
            return CloudSubscriptionOutageWindowFrequency.ThirdMondayOfEachMonth;
            break;
        }
        break;
    }
  }

  return {
    outageWindowFrequency: getFrequency(),
    outageWindowTimeZone: maintenanceWindow.timeZone,
    outageWindowStart: maintenanceWindow.startTime,
    outageWindowEnd: maintenanceWindow.endTime,
  };
}

export enum CloudSubscriptionOutageWindowFrequency {
  Daily = "Daily",
  MondayOfEachWeek = "MondayOfEachWeek",
  TuesdayOfEachWeek = "TuesdayOfEachWeek",
  WednesdayOfEachWeek = "WednesdayOfEachWeek",
  ThursdayOfEachWeek = "ThursdayOfEachWeek",
  FridayOfEachWeek = "FridayOfEachWeek",
  SundayOfEachWeek = "SundayOfEachWeek",
  FirstMondayOfEachMonth = "FirstMondayOfEachMonth",
  SecondMondayOfEachMonth = "SecondMondayOfEachMonth",
  ThirdMondayOfEachMonth = "ThirdMondayOfEachMonth",
}

export const cloudSubscriptionOutageWindowFrequencyDescriptions: Record<
  CloudSubscriptionOutageWindowFrequency,
  string
> = {
  [CloudSubscriptionOutageWindowFrequency.Daily]: "Daily",
  [CloudSubscriptionOutageWindowFrequency.MondayOfEachWeek]: "Monday of each week",
  [CloudSubscriptionOutageWindowFrequency.TuesdayOfEachWeek]: "Tuesday of each week",
  [CloudSubscriptionOutageWindowFrequency.WednesdayOfEachWeek]: "Wednesday of each week",
  [CloudSubscriptionOutageWindowFrequency.ThursdayOfEachWeek]: "Thursday of each week",
  [CloudSubscriptionOutageWindowFrequency.FridayOfEachWeek]: "Friday of each week",
  [CloudSubscriptionOutageWindowFrequency.SundayOfEachWeek]: "Sunday of each week",
  [CloudSubscriptionOutageWindowFrequency.FirstMondayOfEachMonth]: "First Monday of each month",
  [CloudSubscriptionOutageWindowFrequency.SecondMondayOfEachMonth]: "Second Monday of each month",
  [CloudSubscriptionOutageWindowFrequency.ThirdMondayOfEachMonth]: "Third Monday of each month",
};

export enum CloudSubscriptionStatus {
  None = "None",
  Subscribed = "Subscribed",
  Lapsed = "Lapsed",
  Cancelled = "Cancelled",
  Terminated = "Terminated",
}

export interface SubscriptionGroupItem {
  subscriptionGroup: SubscriptionGroupDto | undefined;
}

export interface SubscriptionGroupDto {
  id: string;
  name: string;
}

const BASE_URL = "cloud-subscriptions";

export const getCloudSubscriptionById = async (cloudSubscriptionId: string): Promise<CloudSubscriptionDto> => {
  const response = await axiosClient.get<CloudSubscriptionDto>(`${BASE_URL}/${cloudSubscriptionId}`);
  return response.data;
};

export const getCloudSubscriptionSnapshot = async (
  cloudSubscriptionId: string,
  asOf: DateTime
): Promise<Record<string, unknown>> => {
  const response = await axiosClient.get<Record<string, unknown>>(`${BASE_URL}/${cloudSubscriptionId}/snapshot`, {
    params: { asOf: asOf.toUTC().toISO() },
  });
  return response.data;
};

export const getCloudSubscriptionExistsById = async (cloudSubscriptionId: string): Promise<boolean> => {
  const response = await axiosClient.head(`${BASE_URL}/${cloudSubscriptionId}`, { validateStatus: undefined });
  return response.status === 200;
};

export const getCloudSubscriptionBySerial = async (serialId: string): Promise<CloudSubscriptionDto> => {
  const response = await axiosClient.get<CloudSubscriptionDto>(`${BASE_URL}/serial/${serialId}`);
  return response.data;
};

export interface UpdateCloudSubscriptionRequest {
  entitlements: Record<string, unknown>;
}

export interface UpdateSalesforceCloudSubscriptionRequest extends UpdateCloudSubscriptionRequest {
  salesforceContractId: string;
  expiryDate: DateTime;
}

export interface UpdateZuoraCloudSubscriptionRequest extends UpdateCloudSubscriptionRequest {
  zuoraSubscriptionKey: string;
  nextRenewalDate: DateTime;
}

export interface UpdateInfrastructureCloudSubscriptionRequest extends UpdateCloudSubscriptionRequest {
  comment: string;
}

export interface UpdateTrialCloudSubscriptionRequest extends UpdateCloudSubscriptionRequest {
  expiryDate: DateTime;
}

export const updateSalesforceCloudSubscription = async (
  cloudSubscriptionId: string,
  request: UpdateSalesforceCloudSubscriptionRequest
): Promise<CloudSubscriptionDto> => {
  const result = put<CloudSubscriptionDto>(`${cloudSubscriptionId}/salesforce`, request);
  return result;
};

export const updateZuoraCloudSubscription = async (
  cloudSubscriptionId: string,
  request: UpdateZuoraCloudSubscriptionRequest
): Promise<CloudSubscriptionDto> => {
  const result = put<CloudSubscriptionDto>(`${cloudSubscriptionId}/zuora`, request);
  return result;
};

export const updateInfrastructureCloudSubscription = async (
  cloudSubscriptionId: string,
  request: UpdateInfrastructureCloudSubscriptionRequest
): Promise<CloudSubscriptionDto> => {
  const result = put<CloudSubscriptionDto>(`${cloudSubscriptionId}/infrastructure`, request);
  return result;
};

export const updateTrialCloudSubscription = async (
  cloudSubscriptionId: string,
  request: UpdateTrialCloudSubscriptionRequest
): Promise<CloudSubscriptionDto> => {
  const result = put<CloudSubscriptionDto>(`${cloudSubscriptionId}/trial`, request);
  return result;
};

export type UpdateCloudSubscriptionFeaturesRequest = {
  entitlements: Record<string, EntitlementDto>;
};

export const updateCloudSubscriptionFeatures = async (
  cloudSubscriptionId: string,
  request: UpdateCloudSubscriptionFeaturesRequest
): Promise<CloudSubscriptionDto> => {
  const result = put<CloudSubscriptionDto>(`${cloudSubscriptionId}/features`, request);
  return result;
};

export const changeTaskCap = async (
  cloudSubscriptionId: string,
  taskCap: string,
  applyDuringOutageWindow: boolean
): Promise<void> => {
  const response = await axiosClient.post(`${BASE_URL}/${cloudSubscriptionId}/change-task-cap`, {
    cloudSubscriptionId,
    ...(taskCap === UnlimitedTaskCap ? { isUnlimited: true } : { taskCap }),
    applyDuringOutageWindow,
  });
};

export const updateOutageWindow = async (
  cloudSubscriptionId: string,
  outageWindowFrequency: CloudSubscriptionOutageWindowFrequency,
  outageWindowTimeZone: string,
  outageWindowStart: DateTime,
  outageWindowEnd: DateTime
): Promise<void> => {
  const response = await axiosClient.put(`${BASE_URL}/${cloudSubscriptionId}/outage-window`, {
    outageWindowFrequency,
    outageWindowTimeZone,
    outageWindowStart: outageWindowStart.toLocaleString(DateTime.TIME_24_WITH_SECONDS),
    outageWindowEnd: outageWindowEnd.toLocaleString(DateTime.TIME_24_WITH_SECONDS),
  });
};

export const moveToSalesforce = async (
  cloudSubscriptionId: string,
  salesforceContractId: string,
  expiryDate: DateTime
): Promise<void> => {
  const response = await axiosClient.post(`${BASE_URL}/${cloudSubscriptionId}/licensing-channel/salesforce`, {
    cloudSubscriptionId,
    salesforceContractId,
    expiryDate: expiryDate.toUTC().toISODate(),
  });
};

export const changeUserGrantProtection = async (cloudSubscriptionId: string, isProtected: boolean): Promise<void> => {
  await axiosClient.put(`${BASE_URL}/${cloudSubscriptionId}/user-grant-protection`, {
    cloudSubscriptionId,
    isProtected,
  });
};

export const getHistory = async (cloudSubscriptionId: string): Promise<EntityHistoryReferenceDto[]> => {
  const response = await axiosClient.get(`${BASE_URL}/${cloudSubscriptionId}/history`);
  return response.data;
};

export function isEditableLicensingChannel(licensingChannel: CloudSubscriptionLicensingChannelDto): boolean {
  return (
    isSalesforceContractedLicensingChannel(licensingChannel) ||
    isZuoraSubscriptionLicensingChannel(licensingChannel) ||
    isInfrastructureSubscriptionLicensingChannel(licensingChannel)
  );
}

export function isSalesforceContractedLicensingChannel(
  licensingChannel: CloudSubscriptionLicensingChannelDto
): boolean {
  return licensingChannel.salesforceContractId !== null;
}

export function isTrialLicensingChannel(licensingChannel: CloudSubscriptionLicensingChannelDto): boolean {
  return licensingChannel.trialId !== null;
}

export function isZuoraSubscriptionLicensingChannel(licensingChannel: CloudSubscriptionLicensingChannelDto): boolean {
  return licensingChannel.zuoraSubscriptionKey !== null;
}

export function isInfrastructureSubscriptionLicensingChannel(
  licensingChannel: CloudSubscriptionLicensingChannelDto
): boolean {
  return licensingChannel.renewalPeriod !== null;
}

export function isSubscriptionBasedLicensingChannel(licensingChannel: CloudSubscriptionLicensingChannelDto): boolean {
  return licensingChannel.subscriptionId !== null;
}

export function isImportedTrialLicensingChannel(licensingChannel: CloudSubscriptionLicensingChannelDto): boolean {
  return (
    licensingChannel.expiryDate !== null &&
    !isTrialLicensingChannel(licensingChannel) &&
    !isSalesforceContractedLicensingChannel(licensingChannel) &&
    !isZuoraSubscriptionLicensingChannel(licensingChannel) &&
    !isInfrastructureSubscriptionLicensingChannel(licensingChannel)
  );
}

export function isLegacyBraintreeLicensingChannel(licensingChannel: CloudSubscriptionLicensingChannelDto): boolean {
  return (
    licensingChannel.expiryDate === null &&
    licensingChannel.trialId === null &&
    licensingChannel.salesforceContractId === null &&
    licensingChannel.zuoraSubscriptionKey === null &&
    licensingChannel.renewalPeriod === null &&
    licensingChannel.subscriptionId === null
  );
}

export function isExpiringLicensingChannel(licensingChannel: CloudSubscriptionLicensingChannelDto): boolean {
  return isSalesforceContractedLicensingChannel(licensingChannel) || isTrialLicensingChannel(licensingChannel);
}

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export function isOverrunLimitFeatureDto(x: any): x is OverrunLimitFeatureDto {
  return x.hasOwnProperty("overrun") && x["overrun"];
}

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export function isMachineLimitFeatureDto(x: any): x is MachineLimitFeatureDto {
  return x.hasOwnProperty("machineTypes") && x["machineTypes"];
}

export const activateCloudSubscriptionIpAddressAllowList = async (cloudSubscriptionId: string, activate: boolean) => {
  const response = await axiosClient.put<CloudSubscriptionDto>(
    `${BASE_URL}/${cloudSubscriptionId}/ip-address-allow-list`,
    {
      activate,
    }
  );
  return response.data;
};

export const updateCloudSubscriptionAllowedIpAddress = async (
  cloudSubscriptionId: string,
  existingIpAddress: string | undefined,
  newIpAddress: string,
  description: string
) => {
  const response = await axiosClient.put<CloudSubscriptionDto>(
    `${BASE_URL}/${cloudSubscriptionId}/ip-address-allow-list/${newIpAddress}`,
    {
      existingIpAddress,
      description,
    }
  );
  return response.data;
};

export const deleteCloudSubscriptionAllowedIpAddress = async (cloudSubscriptionId: string, ipAddress: string) => {
  const response = await axiosClient.delete<CloudSubscriptionDto>(
    `${BASE_URL}/${cloudSubscriptionId}/ip-address-allow-list/${ipAddress}`
  );
  return response.data;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function put<T>(url: string, data: any): Promise<T> {
  return axiosClient.put<T>(`${BASE_URL}/${url}`, convertDateTimesToString(data)).then((response) => response.data);
}
